import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FaFacebookF,
  FaTwitter,
  FaWhatsapp,
  FaLinkedinIn,
  FaTelegramPlane,
} from "react-icons/fa";

interface GalleryImage {
  image?: string;
  url?: string;
}

interface PostviewProps {
  id?: number;
  clientId?: number;
  user?: string;
  role?: string;
  time?: string;
  title?: string;
  shortDescription?: string;
  category?: string;
  mainCategory?: string;
  des?: string;
  plainText?: string;
  avater?: string;
  logo_image?: string;
  unique_slug?: string;
  postimage?: string;
  postvideo?: string;
  youtubeUrl?: string;
  galleryImages?: GalleryImage[];
  tags?: any[];
  comments?: any[];
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  isLiked?: boolean;
  postType?: string;
  viewCount?: number;
  shareUrl?: string;
  onDonate?: () => void;
}

// ==========================
// ICON WRAPPER
// ==========================
const FaIcon = ({
  icon: Icon,
  size = 16,
}: any) => {
  return <Icon size={size} />;
};

// ==========================
// API BASE URL
// ==========================
const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// UPLOAD URL HELPER
// ==========================
const getUploadUrl = (
  file?: string | null
) => {
  if (
    !file ||
    String(file).trim() === ""
  ) {
    return "";
  }

  const value =
    String(file).trim();

  // Already full URL
  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  // /uploads/file.jpg
  if (
    value.startsWith("/uploads/")
  ) {
    return encodeURI(
      `${API_BASE_URL}${value}`
    );
  }

  // uploads/file.jpg
  if (
    value.startsWith("uploads/")
  ) {
    return encodeURI(
      `${API_BASE_URL}/${value}`
    );
  }

  // /other/path/file.jpg
  if (
    value.startsWith("/")
  ) {
    return encodeURI(
      `${API_BASE_URL}${value}`
    );
  }

  // filename.jpg
  return encodeURI(
    `${API_BASE_URL}/uploads/${value}`
  );
};

// ==========================
// FIX HTML IMAGE URLS
// ==========================
const fixHtml = (
  html = ""
) => {
  return html
    .replace(
      /<img[^>]*src=["']\/\/:0["'][^>]*>/gi,
      ""
    )
    .replace(
      /<img[^>]*src=["']about:blank["'][^>]*>/gi,
      ""
    )
    .replace(
      /src=["']\/uploads\//gi,
      `src="${API_BASE_URL}/uploads/`
    )
    .replace(
      /src=["']uploads\//gi,
      `src="${API_BASE_URL}/uploads/`
    )
    .replace(
      /&nbsp;/g,
      " "
    );
};

// ==========================
// REMOVE IMAGES FROM HTML
// ==========================
const removeImagesFromHtml = (
  html = ""
) => {
  return html
    .replace(
      /<img[^>]*>/gi,
      ""
    )
    .trim();
};

// ==========================
// YOUTUBE EMBED URL
// ==========================
const getYoutubeEmbedUrl = (
  url?: string
) => {
  if (!url) {
    return "";
  }

  const match =
    url.match(
      /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([^&?/]+)/i
    );

  return match?.[1]
    ? `https://www.youtube.com/embed/${match[1]}`
    : "";
};

const Postview: React.FC<
  PostviewProps
> = ({
  id,
  clientId,

  user = "News Source",

  role,
  time,
  title,
  shortDescription,
  category,
  mainCategory,

  des = "",

  avater =
    "/assets/images/user.png",

  logo_image,
  unique_slug,

  postimage,
  postvideo,
  youtubeUrl,

  likeCount = 0,
  commentCount = 0,
  shareCount = 0,
  viewCount = 0,

  comments = [],

  shareUrl,
  onDonate,
}) => {
  const navigate =
    useNavigate();

  const [
    shareOpen,
    setShareOpen,
  ] = useState(false);

  const [
    showComments,
    setShowComments,
  ] = useState(false);

  // ==========================
  // CLEAN CONTENT
  // ==========================
  const cleanContent =
    fixHtml(des);

  const youtubeEmbedUrl =
    getYoutubeEmbedUrl(
      youtubeUrl
    );

  // ==========================
  // IMAGE / VIDEO URLS
  // ==========================
  const finalLogo =
    getUploadUrl(
      logo_image
    ) ||
    getUploadUrl(
      avater
    ) ||
    "/assets/images/user.png";

  const finalPostImage =
    getUploadUrl(
      postimage
    );

  const finalPostVideo =
    getUploadUrl(
      postvideo
    );

  // ==========================
  // SHARE URL
  // ==========================
  const finalShareUrl =
    shareUrl ||
    `${API_BASE_URL}/share/news-details/${id || ""}`;

  // ==========================
  // DONATION POST
  // ==========================
  const isDonationPost =
    mainCategory
      ?.toLowerCase()
      .trim() ===
    "donation";

  // ==========================
  // MEDIA TYPE
  // ==========================
  const mediaType =
    finalPostImage
      ? "image"
      : youtubeEmbedUrl
      ? "youtube"
      : finalPostVideo
      ? "video"
      : cleanContent.includes(
          "<img"
        )
      ? "content-image"
      : "text";

  // ==========================
  // TEXT CONTENT
  // ==========================
  const textContent =
    mediaType ===
    "content-image"
      ? cleanContent
      : removeImagesFromHtml(
          cleanContent
        );

  // ==========================
  // OPEN POST DETAILS
  // ==========================
  const handleNavigateToDetails =
    () => {
      if (!id) {
        return;
      }

      navigate(
        `/news-details/${id}`
      );
    };

  // ==========================
  // OPEN CLUB
  // ==========================
  const handleClubClick =
    () => {
      if (
        !unique_slug
      ) {
        return;
      }

      navigate(
        `/club/${unique_slug}`
      );
    };

  // ==========================
  // DONATE
  // ==========================
  const handleDonateClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    if (onDonate) {
      onDonate();
      return;
    }

    if (!id) {
      toast.error(
        "Donation post details are unavailable"
      );

      return;
    }

    navigate(
      `/donate/${id}`,
      {
        state: {
          post_id: id,
          client_id:
            clientId,

          main_category:
            mainCategory,

          category,
        },
      }
    );
  };

  // ==========================
  // OPEN SHARE WINDOW
  // ==========================
  const openShare = (
    url: string
  ) => {
    window.open(
      url,
      "_blank",
      "width=700,height=500"
    );
  };

  // ==========================
  // SOCIAL SHARE
  // ==========================
  const handleShare = (
    type: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    const pageTitle =
      title ||
      shortDescription ||
      "Check this post";

    const pageDescription =
      shortDescription ||
      "";

    const encodedUrl =
      encodeURIComponent(
        finalShareUrl
      );

    const encodedText =
      encodeURIComponent(
        pageTitle
      );

    const shareLinks: Record<
      string,
      string
    > = {
      whatsapp:
        `https://wa.me/?text=${encodeURIComponent(
          `${pageTitle}\n\n${pageDescription}\n\n${finalShareUrl}`
        )}`,

      facebook:
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,

      twitter:
        `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,

      telegram:
        `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,

      linkedin:
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };

    const selectedUrl =
      shareLinks[type];

    if (!selectedUrl) {
      return;
    }

    setShareOpen(false);

    openShare(
      selectedUrl
    );
  };

  // ==========================
  // COPY SHARE URL
  // ==========================
  const handleCopyShareLink =
    async (
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation();

      try {
        await navigator.clipboard.writeText(
          finalShareUrl
        );

        toast.success(
          "Link copied"
        );

        setShareOpen(false);
      } catch (
        error
      ) {
        toast.error(
          "Unable to copy link"
        );
      }
    };

  return (
    <>
      <style>
        {`
          .modern-post-card {
            background: #fff;
            border-radius: 24px;
            overflow: visible;
            box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
            border: 1px solid #eef2ff;
            margin-bottom: 24px;
            transition: 0.25s ease;
          }

          .modern-post-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(37, 99, 235, 0.08);
          }

          .modern-post-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 18px 20px 12px;
          }

          .modern-user {
            display: flex;
            align-items: center;
            gap: 14px;
            cursor: pointer;
            min-width: 0;
          }

          .modern-avatar {
            width: 52px;
            height: 52px;
            min-width: 52px;
            border-radius: 16px;
            object-fit: cover;
            border: 3px solid #eff6ff;
            background: #f8fafc;
          }

          .modern-name {
            font-size: 16px;
            font-weight: 600;
            color: #111827;
            margin-bottom: 2px;
          }

          .modern-meta {
            display: flex;
            gap: 8px;
            align-items: center;
            flex-wrap: wrap;
            font-size: 12px;
            color: #6b7280;
          }

          .modern-category {
            background: #eff6ff;
            color: #2563eb;
            padding: 3px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 600;
          }

          .modern-more {
            width: 38px;
            height: 38px;
            border: none;
            border-radius: 12px;
            background: #f8fafc;
            color: #64748b;
            font-size: 18px;
            transition: 0.2s;
            cursor: pointer;
            flex-shrink: 0;
          }

          .modern-more:hover {
            background: #eff6ff;
            color: #2563eb;
          }

          .modern-clickable-content {
            cursor: pointer;
          }

          .modern-caption {
            padding: 0 20px 16px;
          }

          .modern-title {
            font-size: 20px;
            font-weight: 600;
            color: #111827;
            margin-bottom: 8px;
            line-height: 1.4;
          }

          .modern-description {
            color: #4b5563;
            font-size: 14px;
            line-height: 1.8;
          }

          .modern-media {
            position: relative;
            width: 100%;
            background: #f8fafc;
            overflow: hidden;
          }

          .modern-media img,
          .modern-media video {
            width: 100%;
            max-height: 560px;
            object-fit: cover;
            display: block;
          }

          .modern-media iframe {
            display: block;
            width: 100%;
            height: 420px;
            border: none;
          }

          .modern-html {
            padding: 18px 20px 0;
            color: #374151;
            font-size: 15px;
            line-height: 1.9;
            overflow-wrap: anywhere;
          }

          .modern-html img {
            display: block;
            width: 100%;
            max-width: 100%;
            height: auto;
            border-radius: 18px;
            margin-top: 12px;
          }

          .modern-actions {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            margin-top: 14px;
            border-top: 1px solid #f1f5f9;
            position: relative;
          }

          .modern-left-actions {
            display: flex;
            align-items: center;
            gap: 20px;
          }

          .modern-action {
            display: flex;
            align-items: center;
            gap: 7px;
            border: none;
            background: transparent;
            color: #64748b;
            font-size: 14px;
            font-weight: 600;
            transition: 0.2s;
            cursor: pointer;
            padding: 0;
          }

          .modern-action:hover {
            color: #2563eb;
            transform: scale(1.03);
          }

          .modern-action i {
            font-size: 18px;
          }

          .like-active {
            color: #e11d48 !important;
          }

          .heart-fill {
            color: #e11d48;
            font-size: 20px;
            line-height: 1;
          }

          .modern-donate {
            border: none;
            background:
              linear-gradient(
                135deg,
                #2563eb,
                #3b82f6
              );
            color: white;
            font-size: 14px;
            font-weight: 700;
            padding: 10px 18px;
            border-radius: 14px;
            box-shadow:
              0 8px 20px
              rgba(37, 99, 235, 0.2);
            transition: 0.2s;
            cursor: pointer;
          }

          .modern-donate:hover {
            transform: translateY(-1px);
          }

          .modern-floating-donate {
            position: absolute;
            right: 16px;
            bottom: 16px;
            border: none;
            background: white;
            color: #2563eb;
            padding: 10px 16px;
            border-radius: 14px;
            font-size: 13px;
            font-weight: 700;
            box-shadow:
              0 8px 20px
              rgba(0, 0, 0, 0.12);
            cursor: pointer;
            z-index: 2;
          }

          .share-wrapper {
            position: relative;
            display: flex;
            align-items: center;
          }

          .share-social-menu {
            position: absolute;
            bottom: 48px;
            left: 50%;
            transform: translateX(-50%);
            width: 300px;
            background: #fff;
            border-radius: 18px;
            padding: 14px;
            border: 1px solid #eef2ff;
            box-shadow:
              0 16px 38px
              rgba(15, 23, 42, 0.14);
            z-index: 99999;
          }

          .share-social-menu::after {
            content: "";
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform:
              translateX(-50%)
              rotate(45deg);
            width: 16px;
            height: 16px;
            background: #fff;
            border-right:
              1px solid
              #eef2ff;
            border-bottom:
              1px solid
              #eef2ff;
          }

          .share-menu {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin-bottom: 12px;
          }

          .social-icon {
            width: 40px;
            height: 40px;
            border: none;
            border-radius: 50%;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 15px;
            transition: 0.2s ease;
          }

          .social-icon:hover {
            transform: scale(1.08);
          }

          .fb {
            background: #1877f2;
          }

          .tw {
            background: #1da1f2;
          }

          .wa {
            background: #25d366;
          }

          .in {
            background: #0077b5;
          }

          .tg {
            background: #229ed9;
          }

          .share-link-box {
            width: 100%;
            height: 42px;
            background: #101b3d;
            border-radius: 8px;
            display: flex;
            align-items: center;
            overflow: hidden;
          }

          .share-link-box span {
            flex: 1;
            font-size: 12px;
            color: #8ea0c5;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding: 0 12px;
          }

          .share-link-box button {
            height: 100%;
            border: none;
            background: transparent;
            color: #3b82f6;
            font-size: 13px;
            font-weight: 700;
            padding: 0 12px;
            cursor: pointer;
          }

          .comments-panel {
            border-top: 1px solid #eef2f7;
            padding: 16px 20px;
            background: #fafcff;
            border-radius:
              0 0 24px 24px;
          }

          .comments-title {
            font-size: 15px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 12px;
          }

          .comment-row {
            display: flex;
            gap: 10px;
            margin-bottom: 12px;
          }

          .comment-avatar {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            background: #dbeafe;
            color: #2563eb;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            flex-shrink: 0;
          }

          .comment-body {
            flex: 1;
            min-width: 0;
            background: #fff;
            border-radius: 12px;
            padding: 10px 12px;
            border: 1px solid #eef2f7;
          }

          .comment-name {
            font-size: 13px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 4px;
          }

          .comment-text {
            font-size: 13px;
            color: #4b5563;
            line-height: 1.5;
            overflow-wrap: anywhere;
          }

          .comment-date {
            margin-top: 5px;
            font-size: 11px;
            color: #94a3b8;
          }

          .comment-empty {
            text-align: center;
            color: #94a3b8;
            font-size: 13px;
            padding: 10px 0;
          }

          @media (
            max-width: 768px
          ) {
            .modern-post-card {
              border-radius: 18px;
            }

            .modern-post-header {
              padding:
                15px 15px
                10px;
            }

            .modern-avatar {
              width: 46px;
              height: 46px;
              min-width: 46px;
              border-radius: 14px;
            }

            .modern-name {
              font-size: 14px;
            }

            .modern-caption {
              padding:
                0 15px
                14px;
            }

            .modern-title {
              font-size: 17px;
            }

            .modern-description {
              font-size: 13px;
              line-height: 1.65;
            }

            .modern-media iframe {
              height: 250px;
            }

            .modern-html {
              padding:
                14px 15px
                0;
              font-size: 14px;
            }

            .modern-actions {
              padding:
                14px 15px;
              flex-direction: column;
              align-items: flex-start;
              gap: 16px;
            }

            .modern-left-actions {
              width: 100%;
              justify-content:
                space-between;
              gap: 10px;
            }

            .modern-action {
              font-size: 12px;
              gap: 5px;
            }

            .modern-action i {
              font-size: 17px;
            }

            .share-social-menu {
              width: 280px;
              left: auto;
              right: -30px;
              transform: none;
              padding: 12px;
            }

            .share-social-menu::after {
              left: auto;
              right: 43px;
              transform:
                rotate(45deg);
            }

            .social-icon {
              width: 36px;
              height: 36px;
              font-size: 13px;
            }

            .share-link-box {
              height: 40px;
            }

            .modern-donate {
              width: 100%;
            }

            .comments-panel {
              padding:
                14px 15px;
            }
          }

          @media (
            max-width: 420px
          ) {
            .modern-left-actions {
              gap: 7px;
            }

            .modern-action {
              font-size: 11px;
            }

            .modern-floating-donate {
              right: 10px;
              bottom: 10px;
              padding:
                8px 12px;
              font-size: 12px;
            }

            .share-social-menu {
              width: 260px;
              right: -20px;
            }

            .share-menu {
              gap: 8px;
            }
          }
        `}
      </style>

      <article className="modern-post-card">
        {/* ==========================
            HEADER
        ========================== */}
        <div className="modern-post-header">
          <div
            className="modern-user"
            onClick={
              handleClubClick
            }
          >
            <img
              src={
                finalLogo
              }
              className="modern-avatar"
              alt={
                user
              }
              onError={(
                e
              ) => {
                e.currentTarget.src =
                  "/assets/images/user.png";
              }}
            />

            <div>
              <div className="modern-name">
                {user}
              </div>

              <div className="modern-meta">
                {role && (
                  <span>
                    {role}
                  </span>
                )}

                {time && (
                  <span>
                    • {time}
                  </span>
                )}

                {category && (
                  <span className="modern-category">
                    {
                      category
                    }
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="modern-more"
            onClick={(
              e
            ) =>
              e.stopPropagation()
            }
            aria-label="More options"
          >
            ⋯
          </button>
        </div>

        {/* ==========================
            POST CONTENT
        ========================== */}
        <div
          className="modern-clickable-content"
          onClick={
            handleNavigateToDetails
          }
        >
          {(title ||
            shortDescription) && (
            <div className="modern-caption">
              {title && (
                <div className="modern-title">
                  {
                    title
                  }
                </div>
              )}

              {shortDescription && (
                <div className="modern-description">
                  {
                    shortDescription
                  }
                </div>
              )}
            </div>
          )}

          {/* IMAGE */}
          {mediaType ===
            "image" &&
            finalPostImage && (
              <div className="modern-media">
                <img
                  src={
                    finalPostImage
                  }
                  alt={
                    title ||
                    "Post"
                  }
                  onError={(
                    e
                  ) => {
                    e.currentTarget.style.display =
                      "none";
                  }}
                />

                {isDonationPost && (
                  <button
                    type="button"
                    className="modern-floating-donate"
                    onClick={
                      handleDonateClick
                    }
                  >
                    ₹ Donate
                  </button>
                )}
              </div>
            )}

          {/* YOUTUBE */}
          {mediaType ===
            "youtube" && (
              <div className="modern-media">
                <iframe
                  src={
                    youtubeEmbedUrl
                  }
                  title={
                    title ||
                    "YouTube video"
                  }
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />

                {isDonationPost && (
                  <button
                    type="button"
                    className="modern-floating-donate"
                    onClick={
                      handleDonateClick
                    }
                  >
                    ₹ Donate
                  </button>
                )}
              </div>
            )}

          {/* VIDEO */}
          {mediaType ===
            "video" &&
            finalPostVideo && (
              <div className="modern-media">
                <video
                  src={
                    finalPostVideo
                  }
                  controls
                />

                {isDonationPost && (
                  <button
                    type="button"
                    className="modern-floating-donate"
                    onClick={
                      handleDonateClick
                    }
                  >
                    ₹ Donate
                  </button>
                )}
              </div>
            )}

          {/* HTML */}
          {textContent && (
            <div
              className="modern-html"
              dangerouslySetInnerHTML={{
                __html:
                  textContent,
              }}
            />
          )}
        </div>

        {/* ==========================
            ACTIONS
        ========================== */}
        <div className="modern-actions">
          <div className="modern-left-actions">
            {/* VIEWS */}
            <button
              type="button"
              className="modern-action"
              onClick={(
                e
              ) =>
                e.stopPropagation()
              }
            >
              <i className="feather-eye" />

              {
                viewCount
              }
            </button>

            {/* LIKES */}
            <button
              type="button"
              className="modern-action like-active"
              onClick={(
                e
              ) =>
                e.stopPropagation()
              }
            >
              <span className="heart-fill">
                ♥
              </span>

              {
                likeCount
              }
            </button>

            {/* COMMENTS */}
            <button
              type="button"
              className="modern-action comment-button"
              onClick={(
                e
              ) => {
                e.stopPropagation();

                setShowComments(
                  (
                    previous
                  ) =>
                    !previous
                );
              }}
            >
              <i className="feather-message-circle" />

              {
                commentCount
              }
            </button>

            {/* SHARE */}
            <div
              className="share-wrapper"
              onClick={(
                e
              ) =>
                e.stopPropagation()
              }
            >
              <button
                type="button"
                className="modern-action share-button"
                onClick={(
                  e
                ) => {
                  e.stopPropagation();

                  setShareOpen(
                    (
                      previous
                    ) =>
                      !previous
                  );
                }}
              >
                <i className="feather-share-2" />

                {
                  shareCount
                }
              </button>

              {shareOpen && (
                <div className="share-social-menu">
                  <div className="share-menu">
                    <button
                      type="button"
                      onClick={(
                        e
                      ) =>
                        handleShare(
                          "facebook",
                          e
                        )
                      }
                      className="social-icon fb"
                      aria-label="Share on Facebook"
                    >
                      <FaIcon
                        icon={
                          FaFacebookF
                        }
                      />
                    </button>

                    <button
                      type="button"
                      onClick={(
                        e
                      ) =>
                        handleShare(
                          "twitter",
                          e
                        )
                      }
                      className="social-icon tw"
                      aria-label="Share on Twitter"
                    >
                      <FaIcon
                        icon={
                          FaTwitter
                        }
                      />
                    </button>

                    <button
                      type="button"
                      onClick={(
                        e
                      ) =>
                        handleShare(
                          "whatsapp",
                          e
                        )
                      }
                      className="social-icon wa"
                      aria-label="Share on WhatsApp"
                    >
                      <FaIcon
                        icon={
                          FaWhatsapp
                        }
                      />
                    </button>

                    <button
                      type="button"
                      onClick={(
                        e
                      ) =>
                        handleShare(
                          "linkedin",
                          e
                        )
                      }
                      className="social-icon in"
                      aria-label="Share on LinkedIn"
                    >
                      <FaIcon
                        icon={
                          FaLinkedinIn
                        }
                      />
                    </button>

                    <button
                      type="button"
                      onClick={(
                        e
                      ) =>
                        handleShare(
                          "telegram",
                          e
                        )
                      }
                      className="social-icon tg"
                      aria-label="Share on Telegram"
                    >
                      <FaIcon
                        icon={
                          FaTelegramPlane
                        }
                      />
                    </button>
                  </div>

                  <div className="share-link-box">
                    <span>
                      {
                        finalShareUrl
                      }
                    </span>

                    <button
                      type="button"
                      onClick={
                        handleCopyShareLink
                      }
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TEXT DONATION */}
          {isDonationPost &&
            mediaType ===
              "text" && (
              <button
                type="button"
                className="modern-donate"
                onClick={
                  handleDonateClick
                }
              >
                ₹ Donate Now
              </button>
            )}
        </div>

        {/* ==========================
            COMMENTS
        ========================== */}
        {showComments && (
          <div className="comments-panel">
            <div className="comments-title">
              Comments
            </div>

            {Array.isArray(
              comments
            ) &&
            comments.length >
              0 ? (
              comments.map(
                (
                  comment: any,
                  index: number
                ) => {
                  const commentName =
                    comment.full_name ||
                    comment.name ||
                    "Guest";

                  return (
                    <div
                      className="comment-row"
                      key={
                        comment.comment_id ||
                        comment.id ||
                        index
                      }
                    >
                      <div className="comment-avatar">
                        {String(
                          commentName
                        )
                          .charAt(
                            0
                          )
                          .toUpperCase()}
                      </div>

                      <div className="comment-body">
                        <div className="comment-name">
                          {
                            commentName
                          }
                        </div>

                        <div className="comment-text">
                          {comment.comment_text ||
                            comment.comment ||
                            ""}
                        </div>

                        {comment.created_at && (
                          <div className="comment-date">
                            {new Date(
                              comment.created_at
                            ).toLocaleString(
                              "en-IN"
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
              )
            ) : (
              <div className="comment-empty">
                No comments yet
              </div>
            )}
          </div>
        )}
      </article>
    </>
  );
};

export default Postview;